import { computed, inject, Injectable, signal } from '@angular/core';

import type { ReportConfig } from '../domain/report-config.types';
import type {
  ReportError,
  ReportStatus,
} from '../domain/report-status.types';
import { EXCEL_ENGINE } from './excel-report.tokens';

const XLSX_MIME_TYPE =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
const XLSX_EXTENSION = '.xlsx';
const INVALID_FILENAME_CHARS = /[\\/:*?"<>|]/g;
const UNKNOWN_ERROR_MESSAGE = 'Error desconocido';

@Injectable({ providedIn: 'root' })
export class ExcelReportService {
  private readonly engine = inject(EXCEL_ENGINE);

  private readonly _status = signal<ReportStatus>('idle');
  private readonly _error = signal<ReportError | null>(null);

  readonly status = this._status.asReadonly();
  readonly error = this._error.asReadonly();
  readonly isLoading = computed(() => this._status() === 'loading');

  async generate(config: ReportConfig): Promise<void> {
    this._status.set('loading');
    this._error.set(null);

    try {
      const buffer = await this.engine.generate(config);
      this.triggerDownload(buffer, this.buildFileName(config.fileName));
      this._status.set('success');
    } catch (err) {
      this._error.set(this.toReportError(err));
      this._status.set('error');
      throw err;
    }
  }

  reset(): void {
    this._status.set('idle');
    this._error.set(null);
  }

  private toReportError(err: unknown): ReportError {
    const message =
      err instanceof Error ? err.message : UNKNOWN_ERROR_MESSAGE;
    return { message, cause: err };
  }

  private buildFileName(name: string): string {
    const safe = name.replace(INVALID_FILENAME_CHARS, '_');
    return `${safe}${XLSX_EXTENSION}`;
  }

  private triggerDownload(buffer: ArrayBuffer, fileName: string): void {
    const blob = new Blob([buffer], { type: XLSX_MIME_TYPE });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }
}
